import gsap from 'gsap';

import { cubicBezier } from './cubicBezier';

/**
 * The project's easing vocabulary.
 *
 * DESIGN_SYSTEM.md: "Custom GSAP curves only. Never linear."
 *
 * There is deliberately no `linear` entry. GSAP still accepts the string
 * 'none' if something genuinely needs it, such as a continuous marquee,
 * but it cannot be reached by accident through this vocabulary.
 *
 * Control points are shared verbatim with styles/abstracts/_easing.scss.
 * Change them in one place and they must be changed in the other.
 */

/** Ease identifiers. Namespaced so they cannot collide with GSAP built-ins. */
export const EASE = {
  /** Standard UI arrival: quick to leave, gentle to settle. */
  out: 'soh.out',
  /** Standard UI departure. */
  in: 'soh.in',
  /** Symmetric, for elements that leave and return. */
  inOut: 'soh.inOut',
  /** Large, deliberate motion: slow departure, long settle. */
  cinematic: 'soh.cinematic',
  /** Slight overshoot, for objects that should read as physically weighted. */
  backOut: 'soh.backOut',
  /** Sine-shaped in-out with no steep middle, for large cross-fades. */
  gentle: 'soh.gentle',
} as const;

export type EaseName = (typeof EASE)[keyof typeof EASE];

type ControlPoints = readonly [x1: number, y1: number, x2: number, y2: number];

const EASE_CURVES: Readonly<Record<EaseName, ControlPoints>> = {
  [EASE.out]: [0.16, 1, 0.3, 1],
  [EASE.in]: [0.7, 0, 0.84, 0],
  [EASE.inOut]: [0.87, 0, 0.13, 1],
  [EASE.cinematic]: [0.65, 0.05, 0.36, 1],
  [EASE.backOut]: [0.34, 1.56, 0.64, 1],
  [EASE.gentle]: [0.37, 0, 0.63, 1],
};

/**
 * Registers every ease with GSAP under its namespaced name.
 *
 * Idempotent: GSAP overwrites an ease registered twice under the same name,
 * so a repeated call (React Strict Mode, Fast Refresh) is harmless.
 *
 * Called by AnimationManager.init() — not at module scope, so that merely
 * importing EASE for its type does not have a side effect.
 */
export const registerEases = (): void => {
  for (const name of Object.keys(EASE_CURVES) as EaseName[]) {
    const [x1, y1, x2, y2] = EASE_CURVES[name];
    gsap.registerEase(name, cubicBezier(x1, y1, x2, y2));
  }
};
